/**
 * The waitlist form's choices: one list, mirrored value for value by the backend's body model for
 * POST /api/public/waitlist (platform, audience_band, help_with). Change a value here only together with the
 * backend; a value it doesn't know comes back as a 422 on that field.
 */

export type WaitlistPlatform = 'youtube' | 'instagram' | 'tiktok' | 'twitch' | 'other';
export type AudienceBand = 'lt_1k' | '1k_10k' | '10k_100k' | '100k_1m' | 'gt_1m';
export type HelpTopic = 'finding_sponsors' | 'pitching' | 'pricing' | 'contracts' | 'getting_paid' | 'reporting' | 'other';
export type PlanInterest = 'free' | 'plus' | 'pro';

export type PlatformOption = {
  value: WaitlistPlatform;
  label: string;
  /** the channel field's placeholder once this platform is picked */
  channelPlaceholder: string;
  /** what this platform calls an audience, for the size question */
  audienceWord: 'subscribers' | 'followers' | null;
};

export const PLATFORMS: readonly PlatformOption[] = [
  { value: 'youtube', label: 'YouTube', channelPlaceholder: 'youtube.com/@yourchannel', audienceWord: 'subscribers' },
  { value: 'instagram', label: 'Instagram', channelPlaceholder: '@yourhandle', audienceWord: 'followers' },
  { value: 'tiktok', label: 'TikTok', channelPlaceholder: '@yourhandle', audienceWord: 'followers' },
  { value: 'twitch', label: 'Twitch', channelPlaceholder: 'twitch.tv/yourname', audienceWord: 'followers' },
  { value: 'other', label: 'Somewhere else', channelPlaceholder: 'Link to your work', audienceWord: null },
];

/** before a platform is picked */
export const CHANNEL_PLACEHOLDER_DEFAULT = 'Link or @handle';

export const AUDIENCE_BANDS: readonly { value: AudienceBand; label: string }[] = [
  { value: 'lt_1k', label: 'Under 1K' },
  { value: '1k_10k', label: '1K–10K' },
  { value: '10k_100k', label: '10K–100K' },
  { value: '100k_1m', label: '100K–1M' },
  { value: 'gt_1m', label: '1M+' },
];

export const HELP_TOPICS: readonly { value: HelpTopic; label: string }[] = [
  { value: 'finding_sponsors', label: 'Finding sponsors' },
  { value: 'pitching', label: 'Pitching brands' },
  { value: 'pricing', label: 'Pricing a deal' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'getting_paid', label: 'Getting paid' },
  { value: 'reporting', label: 'Reporting results' },
  { value: 'other', label: 'Something else' },
];

export const PLAN_INTERESTS: readonly PlanInterest[] = ['free', 'plus', 'pro'];

export const LIMITS = { first_name: 80, email: 254, channel: 200, help_other: 200 } as const;

/** The size question, worded the way the picked platform counts its audience. */
export function audienceQuestion(platform: WaitlistPlatform | ''): string {
  const word = PLATFORMS.find((p) => p.value === platform)?.audienceWord;
  return word ? `Roughly how many ${word} there?` : 'Roughly how big is your audience?';
}

export function channelPlaceholder(platform: WaitlistPlatform | ''): string {
  return PLATFORMS.find((p) => p.value === platform)?.channelPlaceholder ?? CHANNEL_PLACEHOLDER_DEFAULT;
}
